import type {
  CreatePurchaseOrderPayload,
  UpdatePurchaseOrderDraftPayload,
} from "@/modules/inventory/domain/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  inventoryPurchaseOrdersApi,
  type PurchaseOrdersListParams,
  type ReceivePurchaseOrderPayload,
} from "./purchase-orders-client";

export const purchaseOrdersKeys = {
  all: ["inventory", "purchase-orders"] as const,
  list: (params: PurchaseOrdersListParams) =>
    ["inventory", "purchase-orders", "list", params] as const,
  detail: (id: string) =>
    ["inventory", "purchase-orders", "detail", id] as const,
};

function invalidatePo(qc: ReturnType<typeof useQueryClient>, id?: string) {
  qc.invalidateQueries({ queryKey: purchaseOrdersKeys.all });
  if (id) {
    qc.invalidateQueries({ queryKey: purchaseOrdersKeys.detail(id) });
  }
  qc.invalidateQueries({ queryKey: ["inventory", "lots"] });
  qc.invalidateQueries({ queryKey: ["inventory", "movements"] });
}

export function usePurchaseOrderSuggestedLinesQuery(supplierId: string) {
  return useQuery({
    queryKey: [
      "inventory",
      "purchase-orders",
      "suggested-lines",
      supplierId || "all",
    ] as const,
    queryFn: () =>
      inventoryPurchaseOrdersApi.suggestedLines({
        supplierId: supplierId || undefined,
        limit: 15,
      }),
  });
}

export function usePurchaseOrdersQuery(params: PurchaseOrdersListParams) {
  return useQuery({
    queryKey: purchaseOrdersKeys.list(params),
    queryFn: () => inventoryPurchaseOrdersApi.list(params),
  });
}

export function usePurchaseOrderQuery(id: string) {
  return useQuery({
    queryKey: purchaseOrdersKeys.detail(id),
    queryFn: () => inventoryPurchaseOrdersApi.get(id),
    enabled: !!id,
  });
}

export function useCreatePurchaseOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreatePurchaseOrderPayload) =>
      inventoryPurchaseOrdersApi.create(payload),
    onSuccess: () => invalidatePo(qc),
  });
}

export function useUpdatePurchaseOrderDraft(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdatePurchaseOrderDraftPayload) =>
      inventoryPurchaseOrdersApi.updateDraft(id, payload),
    onSuccess: () => invalidatePo(qc, id),
  });
}

export function useSubmitPurchaseOrder(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => inventoryPurchaseOrdersApi.submit(id),
    onSuccess: () => invalidatePo(qc, id),
  });
}

export function useApprovePurchaseOrder(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => inventoryPurchaseOrdersApi.approve(id),
    onSuccess: () => invalidatePo(qc, id),
  });
}

export function useRejectPurchaseOrder(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => inventoryPurchaseOrdersApi.reject(id),
    onSuccess: () => invalidatePo(qc, id),
  });
}

export function useCancelPurchaseOrder(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => inventoryPurchaseOrdersApi.cancel(id),
    onSuccess: () => invalidatePo(qc, id),
  });
}

export function useClosePurchaseOrder(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => inventoryPurchaseOrdersApi.close(id),
    onSuccess: () => invalidatePo(qc, id),
  });
}

export function useReceivePurchaseOrder(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ReceivePurchaseOrderPayload) =>
      inventoryPurchaseOrdersApi.receive(id, payload),
    onSuccess: () => invalidatePo(qc, id),
  });
}
